import { toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/auth-context';
import { useBulkEmailBatchStatusQuery } from '@/hooks/queries/use-bulk-email-batch-status-query';
import useBulkEmailProgressStore from '@/stores/bulk-email-progress-store';
import { BulkEmailBatchStatus } from '@fluenceur/types';
import { HStack, Progress, Text, VStack } from '@chakra-ui/react';
import { memo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const BATCH_CLEAR_DELAY_MS = 100;
const PROGRESS_TOAST_DELAY_MS = 150;
const PROGRESS_TOAST_ID = 'bulk-email-progress';

/**
 * Global manager for bulk email progress toasts.
 * Follows the same pattern as BulkInviteProgressManager.
 */
export const BulkEmailProgressManager: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const previousBrandRef = useRef(user?.currentBrandId);
  const brandChanged = previousBrandRef.current !== user?.currentBrandId;
  const activeBatch = useBulkEmailProgressStore((state) => state.activeBatch);
  const clearBatch = useBulkEmailProgressStore((state) => state.clearBatch);
  const updateProgress = useBulkEmailProgressStore((state) => state.updateProgress);
  const completeBatch = useBulkEmailProgressStore((state) => state.completeBatch);
  const markUnknown = useBulkEmailProgressStore((state) => state.markUnknown);
  const failBatch = useBulkEmailProgressStore((state) => state.failBatch);
  const batchStatusQuery = useBulkEmailBatchStatusQuery(
    user?.currentBrandId,
    activeBatch?.batchId ?? null,
    activeBatch?.status === 'processing' && !brandChanged
  );

  const hasCompletedRef = useRef(false);
  const progressToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearBatchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const progressToastShownRef = useRef(false);

  useEffect(() => {
    if (!brandChanged) return;
    previousBrandRef.current = user?.currentBrandId;
    clearBatch();
    toaster.dismiss(PROGRESS_TOAST_ID);
  }, [brandChanged, clearBatch, user?.currentBrandId]);

  useEffect(() => {
    if (activeBatch?.status === 'processing') {
      hasCompletedRef.current = false;
      progressToastShownRef.current = false;
      if (clearBatchTimerRef.current) {
        clearTimeout(clearBatchTimerRef.current);
        clearBatchTimerRef.current = null;
      }
    }
  }, [activeBatch?.batchId, activeBatch?.status]);

  useEffect(() => {
    return () => {
      if (progressToastTimerRef.current) {
        clearTimeout(progressToastTimerRef.current);
      }
      if (clearBatchTimerRef.current) {
        clearTimeout(clearBatchTimerRef.current);
      }
      toaster.dismiss(PROGRESS_TOAST_ID);
    };
  }, []);

  // Pusher is an optimisation, not the source of truth. The shared status query
  // keeps the active campaign accurate when a real-time event is unavailable.
  useEffect(() => {
    const batch = batchStatusQuery.data;
    if (brandChanged || !activeBatch || activeBatch.status !== 'processing' || !batch) return;
    if (batch.id !== activeBatch.batchId) return;

    if (batch.status === BulkEmailBatchStatus.UNKNOWN) {
      markUnknown(batch.id, batch.sentCount, batch.failedCount);
      return;
    }
    if (batch.status === BulkEmailBatchStatus.FAILED) {
      // No recipient-level delivery result exists for an undispatched batch.
      // Never infer failures from the total.
      failBatch(batch.id);
      return;
    }

    updateProgress(batch.id, batch.sentCount, batch.failedCount);
    if (batch.status === BulkEmailBatchStatus.COMPLETED) {
      completeBatch(batch.id, batch.sentCount, batch.failedCount);
    }
  }, [
    activeBatch,
    batchStatusQuery.data,
    brandChanged,
    completeBatch,
    failBatch,
    updateProgress,
    markUnknown,
  ]);

  useEffect(() => {
    if (brandChanged) return;
    if (!activeBatch) {
      if (progressToastTimerRef.current) {
        clearTimeout(progressToastTimerRef.current);
        progressToastTimerRef.current = null;
      }
      toaster.dismiss(PROGRESS_TOAST_ID);
      return;
    }

    const { total, sent, failed, status } = activeBatch;
    const current = sent + failed;

    if (status === 'completed' && !hasCompletedRef.current) {
      hasCompletedRef.current = true;

      if (progressToastTimerRef.current) {
        clearTimeout(progressToastTimerRef.current);
        progressToastTimerRef.current = null;
      }

      if (progressToastShownRef.current) {
        toaster.dismiss(PROGRESS_TOAST_ID);
      }

      const allSucceeded = failed === 0;
      toaster.create({
        title: allSucceeded
          ? t('bulkEmail.progress.success.title')
          : t('bulkEmail.progress.partialSuccess.title'),
        description: allSucceeded
          ? t('bulkEmail.progress.success.description', { count: sent })
          : t('bulkEmail.progress.partialSuccess.description', { succeeded: sent, failed }),
        type: allSucceeded ? 'success' : 'warning',
        meta: { closable: true },
      });

      clearBatchTimerRef.current = setTimeout(
        () => clearBatch(activeBatch.batchId),
        BATCH_CLEAR_DELAY_MS
      );
      return;
    }

    if ((status === 'error' || status === 'unknown') && !hasCompletedRef.current) {
      hasCompletedRef.current = true;

      if (progressToastTimerRef.current) {
        clearTimeout(progressToastTimerRef.current);
        progressToastTimerRef.current = null;
      }
      if (progressToastShownRef.current) {
        toaster.dismiss(PROGRESS_TOAST_ID);
      }

      toaster.create({
        title: t(
          status === 'unknown'
            ? 'bulkEmail.progress.unknown.title'
            : 'bulkEmail.progress.preparationFailed.title'
        ),
        description: t(
          status === 'unknown'
            ? 'errors.E_EMAIL_SEND_OUTCOME_UNKNOWN'
            : 'bulkEmail.progress.preparationFailed.description'
        ),
        type: status === 'unknown' ? 'warning' : 'error',
        meta: { closable: true },
      });
      clearBatchTimerRef.current = setTimeout(
        () => clearBatch(activeBatch.batchId),
        BATCH_CLEAR_DELAY_MS
      );
      return;
    }

    if (status === 'processing') {
      const hasReceivedProgress = current > 0;

      if (progressToastShownRef.current) {
        toaster.dismiss(PROGRESS_TOAST_ID);
        toaster.create({
          id: PROGRESS_TOAST_ID,
          title: t('bulkEmail.progress.title'),
          description: (
            <ProgressContent
              current={current}
              total={total}
              sent={sent}
              failed={failed}
              status={status}
            />
          ),
          type: 'loading',
          duration: Infinity,
        });
      } else if (hasReceivedProgress && !progressToastTimerRef.current) {
        progressToastTimerRef.current = setTimeout(() => {
          progressToastTimerRef.current = null;
          progressToastShownRef.current = true;
          toaster.create({
            id: PROGRESS_TOAST_ID,
            title: t('bulkEmail.progress.title'),
            description: (
              <ProgressContent
                current={current}
                total={total}
                sent={sent}
                failed={failed}
                status={status}
              />
            ),
            type: 'loading',
            duration: Infinity,
          });
        }, PROGRESS_TOAST_DELAY_MS);
      }
    }
  }, [activeBatch, brandChanged, t, clearBatch]);

  return null;
};

interface ProgressContentProps {
  current: number;
  total: number;
  sent: number;
  failed: number;
  status: 'processing' | 'completed' | 'error';
}

const ProgressContent = memo<ProgressContentProps>(({ current, total, sent, failed, status }) => {
  const { t } = useTranslation();
  const percentage = total > 0 ? Math.round((current / total) * 100) : 0;

  return (
    <VStack align="stretch" gap={2} w="full" minW="250px">
      <Progress.Root value={percentage} size="sm" colorPalette="primary">
        <Progress.Track>
          <Progress.Range />
        </Progress.Track>
      </Progress.Root>
      <HStack justify="space-between" fontSize="xs" color="fg.muted">
        <Text>
          {status === 'processing'
            ? t('bulkEmail.progress.sending', { current, total })
            : t('bulkEmail.progress.done', { current, total })}
        </Text>
        <Text>{percentage}%</Text>
      </HStack>
      {(sent > 0 || failed > 0) && (
        <HStack gap={3} fontSize="xs">
          {sent > 0 && (
            <Text color="fg.success">{t('bulkEmail.progress.succeeded', { count: sent })}</Text>
          )}
          {failed > 0 && (
            <Text color="fg.error">{t('bulkEmail.progress.failed', { count: failed })}</Text>
          )}
        </HStack>
      )}
    </VStack>
  );
});

ProgressContent.displayName = 'BulkEmailProgressContent';

export default BulkEmailProgressManager;
